import { Accordion } from '@/atoms/Accordion';
import { MoreButton } from '@/atoms/MoreButton';
import { SelectFilter } from '@/atoms/SelectFilter';
import { useAdaptiveLayout } from '@/hooks/useAdaptiveLayout';
import {
  FilterFieldsType,
  FilterType,
  InStockFilterValues,
  RangeFilterObjectType,
  SelectFilterObjectType,
  SortFilterType,
} from '@/types';
import { useState } from 'react';
import { RangeFilter } from '../RangeFilter';
import { Toggle } from '../Toggle';
import { Button } from '@/atoms/Button';
import styles from './FilterContent.module.scss';

const SORT_SCREEN_CODE = 'SORT_SCREEN_CODE';

type FilterContentProps = {
  filters: FilterFieldsType;
  toggleDrawer: () => void;
  onApply: () => void;
  selects: SelectFilterObjectType;
  onChangeSelect: (code: string, value: string) => void;
  onChangeRange: (code: string, min: number, max: number) => void;
  onReset: () => void;
  ranges: RangeFilterObjectType;
  inStock: InStockFilterValues;
  onChangeStock: (value: InStockFilterValues) => void;
  sorts: SortFilterType[];
  activeSort: SortFilterType;
  onChangeSort: (sort: SortFilterType) => void;
};

export const FilterContent = ({
  filters,
  toggleDrawer,
  onApply,
  selects,
  onChangeSelect,
  onChangeRange,
  onReset,
  ranges,
  inStock,
  onChangeStock,
  sorts,
  activeSort,
  onChangeSort,
}: FilterContentProps) => {
  const { isTablet } = useAdaptiveLayout();
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);

  const handleMoreButtonClick = (filterCode: string) => {
    setSelectedFilter(filterCode);
  };

  const handleBack = () => {
    setSelectedFilter(null);
  };

  const handleReset = () => {
    onReset();
    setSelectedFilter(null);
    toggleDrawer();
  };

  const handleInStockChange = (value: boolean) => {
    if (value) {
      onChangeStock(InStockFilterValues.InStock);
    } else {
      onChangeStock(InStockFilterValues.OutOfStock);
    }
  };

  const renderDrawerHeading = () => {
    const activeCount =
      (selectedFilter && selects[selectedFilter]?.values.length) || 0;

    if (selectedFilter) {
      return (
        <div className={styles.drawerHeadingChosen} onClick={handleBack}>
          <img
            src="/svg/arrow-back.svg"
            alt="back-button"
            className={styles.closeButton}
          />
          {selectedFilter === SORT_SCREEN_CODE ? (
            <span className={styles.title}>Сортировка</span>
          ) : (
            <span className={styles.title}>
              {filters[selectedFilter].name}{' '}
              {activeCount > 0 && `(${activeCount})`}
            </span>
          )}
        </div>
      );
    }

    return (
      <div className={styles.drawerHeading}>
        <span className={styles.title}>Фильтры</span>
        <img
          src="/svg/close.svg"
          alt="close-button"
          onClick={toggleDrawer}
          className={styles.closeButton}
        />
      </div>
    );
  };

  const renderFilterContent = () => {
    if (selectedFilter === SORT_SCREEN_CODE) {
      return (
        <div className={styles.sortScreen}>
          {sorts.map((sort) => (
            <div
              key={sort.code}
              className={styles.sortItem}
              onClick={() => onChangeSort(sort)}
            >
              <span className={styles.sortItemName}>{sort.name}</span>
              {sort.code === activeSort.code && (
                <img
                  src="/svg/check.svg"
                  alt="check"
                  className={styles.check}
                />
              )}
            </div>
          ))}
        </div>
      );
    }

    if (selectedFilter) {
      const filter = filters[selectedFilter];

      if (filter.displayType === FilterType.Select) {
        return (
          <SelectFilter
            filter={filter}
            onChange={onChangeSelect}
            values={selects}
          />
        );
      }

      return null;
    }

    return Object.values(filters).map((filter) => {
      if (
        filter.displayType === FilterType.Select &&
        !isTablet &&
        filter.values.length > 0
      ) {
        return (
          <Accordion title={filter.name} key={filter.code}>
            <div className={styles.select}>
              <SelectFilter
                filter={filter}
                onChange={onChangeSelect}
                values={selects}
              />
            </div>
          </Accordion>
        );
      }

      if (
        filter.displayType === FilterType.Select &&
        !!isTablet &&
        filter.values.length > 0
      ) {
        return (
          <MoreButton
            text={filter.name}
            key={filter.code}
            onClick={() => handleMoreButtonClick(filter.code)}
          />
        );
      }

      if (
        filter.displayType === FilterType.Range ||
        filter.displayType === FilterType.PriceRange
      ) {
        return (
          <RangeFilter
            key={filter.code}
            filter={filter}
            onChange={onChangeRange}
            title={filter.name}
            className={styles.range}
            values={ranges}
          />
        );
      }

      return null;
    });
  };

  return (
    <div className={styles.content}>
      {renderDrawerHeading()}
      {!selectedFilter && (
        <>
          <Toggle
            checked={inStock === InStockFilterValues.InStock}
            onChange={handleInStockChange}
            label="В наличии"
          />
          <MoreButton
            text={`Сначала ${activeSort.name.toLowerCase()}`}
            onClick={() => handleMoreButtonClick(SORT_SCREEN_CODE)}
            className={styles.sortButton}
          />
        </>
      )}
      <div className={styles.drawerFilters}>{renderFilterContent()}</div>
      <div className={styles.buttons}>
        <Button variant="primary" text="Применить" onClick={onApply} />
        {!selectedFilter && (
          <Button variant="secondary" text="Сбросить" onClick={handleReset} />
        )}
      </div>
    </div>
  );
};
